import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, EchecApi } from '../partage/api/client';
import type { CleDuCompte, ConnexionDuCompte } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { useDeconnexion } from '../partage/useArbre';
import {
  AUCUNE_CONNEXION_DE_CLAUDE,
  AUCUNE_IA_BRANCHEE,
  CE_QUI_EST_GARDE,
  CETTE_SESSION,
  CONFIDENTIALITE,
  CONFIRMATION_DE_LA_COUPURE,
  CONFIRMATION_DE_LA_REVOCATION,
  CONNEXIONS_DE_CLAUDE,
  COUPER_LA_CONNEXION,
  COUPER_LA_SESSION,
  E_MAIL_DU_COMPTE,
  ENVOYER_LE_LIEN,
  EXPORTER_TOUT,
  GARDER_LA_CLE,
  GARDER_LA_CONNEXION,
  GARDER_MON_COMPTE,
  GESTE_SANS_RESEAU,
  PAS_DE_REPONSE,
  REFUS_DU_DERNIER_COMPTE,
  REFUS_DU_PREMIER_COMPTE,
  REFUS_SANS_RAISON,
  REGARDE_TA_BOITE_MAIL,
  REVOQUER,
  SANS_E_MAIL,
  SESSION_ACTIVE_MAINTENANT,
  SORTIR,
  SUPPRIMER_MON_COMPTE,
  UN_INSTANT,
  TROP_DE_TENTATIVES,
  confirmationDeLaSuppression,
  couperLaConnexion,
  couperLaSession,
  depuisQuandEnMots,
  revoquerLaCle,
  viesDUneCle,
  viesDUneConnexion,
} from '../partage/vocabulaire';
import { raisonDuRefusDeSuppression, type RaisonDuRefus } from './suppression';
import './OngletsDuCompte.css';

/**
 * Le contenu des onglets Compte, IA branchée et Mes données de Paramètres, à l'ordinateur
 * (chantier parametres-du-compte, ticket 03 ; maquette du carnet `parametres.html`, les feuilles
 * pâles du bas : `.session`, `.couper`, `.btn-l`, `.btn-l.danger`). La feuille (`.fp`) et son titre
 * sont posés par `Parametres` (sujet apparence) ; ses lignes de réglage (`.reg-l`) sont les
 * siennes, `parametres-ligne`. Tout ce qui est montré vient du serveur ; l'écran ne garde que ce
 * qui est ouvert (une confirmation) et la réponse d'un geste.
 */

const RAISONS: Record<RaisonDuRefus, string> = {
  premier: REFUS_DU_PREMIER_COMPTE,
  dernier: REFUS_DU_DERNIER_COMPTE,
  inconnue: REFUS_SANS_RAISON,
};

function messageDEchec(erreur: unknown): string {
  if (erreur instanceof EchecApi && erreur.code === 'hors_ligne') return GESTE_SANS_RESEAU;
  if (erreur instanceof EchecApi && erreur.statut === 429) return TROP_DE_TENTATIVES;
  return PAS_DE_REPONSE;
}

/** Compte : l'e-mail, les sessions en cours (celle-ci marquée, les autres à couper), « Sortir ». */
export function OngletCompte({ email }: { email: string | null }) {
  const client = useQueryClient();
  const sessions = useQuery({ queryKey: cles.sessionsDuCompte, queryFn: api.sessionsDuCompte });
  const deconnexion = useDeconnexion();
  const couper = useMutation({
    mutationFn: api.couperUneSession,
    // Coupée, ou déjà partie (404) : la liste relue dit ce qui reste.
    onSettled: () => client.invalidateQueries({ queryKey: cles.sessionsDuCompte }),
    onSuccess: (reponse) => {
      // « cette session » ne se coupe pas d'ici, mais si c'était elle, l'app est sortie.
      if (reponse.ici) window.location.assign('/connexion');
    },
  });

  return (
    <>
      <div className="parametres-ligne compte-premiere-ligne">
        <span>{E_MAIL_DU_COMPTE}</span>
        <b>{email ?? SANS_E_MAIL}</b>
      </div>
      {sessions.data?.sessions.map((session) => (
        <div key={session.numero} className="compte-session" data-session={session.numero}>
          {session.appareil}
          <small>{session.ici ? SESSION_ACTIVE_MAINTENANT : depuisQuandEnMots(session.derniereActivite)}</small>
          {session.ici ? (
            <span className="compte-couper">{CETTE_SESSION}</span>
          ) : (
            <button
              type="button"
              className="compte-couper"
              aria-label={couperLaSession(session.appareil)}
              disabled={couper.isPending}
              onClick={() => couper.mutate(session.numero)}
            >
              {COUPER_LA_SESSION}
            </button>
          )}
        </div>
      ))}
      {sessions.isError && <p className="compte-message">{messageDEchec(sessions.error)}</p>}
      <button type="button" className="compte-bouton" onClick={() => deconnexion.mutate()} disabled={deconnexion.isPending}>
        {SORTIR}
      </button>
    </>
  );
}

/**
 * IA branchée : les clés d'accès, puis, dessous, les connexions de Claude (chantier
 * connecteur-claude, ticket 04). Ni clé ni connexion : une seule phrase.
 */
export function OngletIABranchee() {
  const lesCles = useQuery({ queryKey: cles.clesDuCompte, queryFn: api.clesDuCompte });
  const lesConnexions = useQuery({ queryKey: cles.connexionsDeClaude, queryFn: api.connexionsDeClaude });

  const echec = lesCles.error ?? lesConnexions.error;
  if (echec) return <p className="compte-message">{messageDEchec(echec)}</p>;
  if (!lesCles.data || !lesConnexions.data) return null;
  if (lesCles.data.cles.length === 0 && lesConnexions.data.connexions.length === 0) {
    return <p className="parametres-explication compte-vide">{AUCUNE_IA_BRANCHEE}</p>;
  }

  return (
    <>
      <ClesDAcces lesCles={lesCles.data.cles} />
      <ConnexionsDeClaude connexions={lesConnexions.data.connexions} />
    </>
  );
}

/** Chaque clé d'accès, ses dates, « Révoquer » après confirmation ; une révoquée en pâle. */
function ClesDAcces({ lesCles }: { lesCles: CleDuCompte[] }) {
  const client = useQueryClient();
  const [aRevoquer, setARevoquer] = useState<string | null>(null);
  const revoquer = useMutation({
    mutationFn: api.revoquerUneCle,
    onSettled: () => {
      setARevoquer(null);
      return client.invalidateQueries({ queryKey: cles.clesDuCompte });
    },
  });

  return (
    <>
      {lesCles.map((cle, rang) => {
        const revoquee = cle.revoqueeLe !== null;
        return (
          <div key={`${cle.nom}-${rang}`} className={revoquee ? 'compte-cle est-revoquee' : 'compte-cle'} data-cle={cle.nom}>
            <div className="compte-session">
              {cle.nom}
              <small>{viesDUneCle(cle)}</small>
              {!revoquee && aRevoquer !== cle.nom && (
                <button type="button" className="compte-couper" aria-label={revoquerLaCle(cle.nom)} onClick={() => setARevoquer(cle.nom)}>
                  {REVOQUER}
                </button>
              )}
            </div>
            {!revoquee && aRevoquer === cle.nom && (
              <div className="compte-confirmation" role="group" aria-label={revoquerLaCle(cle.nom)}>
                <p>{CONFIRMATION_DE_LA_REVOCATION}</p>
                <button
                  type="button"
                  className="compte-bouton compte-bouton--danger"
                  disabled={revoquer.isPending}
                  onClick={() => revoquer.mutate(cle.nom)}
                >
                  {revoquer.isPending ? UN_INSTANT : REVOQUER}
                </button>
                <button type="button" className="compte-bouton" onClick={() => setARevoquer(null)}>
                  {GARDER_LA_CLE}
                </button>
              </div>
            )}
          </div>
        );
      })}
      {revoquer.isError && <p className="compte-message">{messageDEchec(revoquer.error)}</p>}
    </>
  );
}

/** Une connexion déjà partie (404 : coupée ailleurs, ou tombée) : rien à dire, la liste relue suffit. */
const dejaPartie = (erreur: unknown) => erreur instanceof EchecApi && erreur.statut === 404;

/**
 * Les connexions de Claude (ticket 04) : le nom du client, l'ouverture, la dernière utilisation,
 * « Couper » confirmé sous la ligne, comme « Révoquer ». Coupée, elle est effacée par le serveur :
 * la liste relue ne la montre plus. Sans connexion, une phrase.
 */
function ConnexionsDeClaude({ connexions }: { connexions: ConnexionDuCompte[] }) {
  const client = useQueryClient();
  const [aCouper, setACouper] = useState<string | null>(null);
  const couper = useMutation({
    mutationFn: api.couperUneConnexion,
    // Coupée, ou déjà partie (404) : la liste relue dit ce qui reste.
    onSettled: () => {
      setACouper(null);
      return client.invalidateQueries({ queryKey: cles.connexionsDeClaude });
    },
  });

  return (
    <section className="compte-connexions" aria-label={CONNEXIONS_DE_CLAUDE}>
      <p className="compte-intertitre">{CONNEXIONS_DE_CLAUDE}</p>
      {connexions.length === 0 && <p className="parametres-explication compte-vide">{AUCUNE_CONNEXION_DE_CLAUDE}</p>}
      {connexions.map((connexion) => (
        <div key={connexion.id} className="compte-cle" data-connexion={connexion.id}>
          <div className="compte-session">
            {connexion.nomDuClient}
            <small>{viesDUneConnexion(connexion)}</small>
            {aCouper !== connexion.id && (
              <button
                type="button"
                className="compte-couper"
                aria-label={couperLaConnexion(connexion.nomDuClient)}
                onClick={() => setACouper(connexion.id)}
              >
                {COUPER_LA_CONNEXION}
              </button>
            )}
          </div>
          {aCouper === connexion.id && (
            <div className="compte-confirmation" role="group" aria-label={couperLaConnexion(connexion.nomDuClient)}>
              <p>{CONFIRMATION_DE_LA_COUPURE}</p>
              <button
                type="button"
                className="compte-bouton compte-bouton--danger"
                disabled={couper.isPending}
                onClick={() => couper.mutate(connexion.id)}
              >
                {couper.isPending ? UN_INSTANT : COUPER_LA_CONNEXION}
              </button>
              <button type="button" className="compte-bouton" onClick={() => setACouper(null)}>
                {GARDER_LA_CONNEXION}
              </button>
            </div>
          )}
        </div>
      ))}
      {couper.isError && !dejaPartie(couper.error) && <p className="compte-message">{messageDEchec(couper.error)}</p>}
    </section>
  );
}

type EtatDeLaSuppression = 'repos' | 'confirmer' | 'envoi' | 'envoye' | { refus: RaisonDuRefus } | { panne: string };

/**
 * Mes données : tout télécharger, la page Confidentialité, et « Supprimer mon compte », qui ne
 * fait qu'envoyer un mail (un vol de session ne doit pas suffire à effacer, décision 1 d'Igor du
 * 2026-10-20). Le premier et le dernier compte sont refusés : la phrase du refus prend la place
 * du bouton.
 */
export function OngletMesDonnees({ email }: { email: string | null }) {
  const [etat, setEtat] = useState<EtatDeLaSuppression>('repos');

  async function envoyer() {
    setEtat('envoi');
    try {
      await api.demanderLaSuppression();
      setEtat('envoye');
    } catch (erreur) {
      const refus = raisonDuRefusDeSuppression(erreur);
      setEtat(refus ? { refus } : { panne: messageDEchec(erreur) });
    }
  }

  return (
    <>
      <a className="compte-bouton" href={api.urlExportDuCompte} download>
        {EXPORTER_TOUT}
      </a>
      {(etat === 'repos' || (typeof etat === 'object' && 'panne' in etat)) && (
        <button type="button" className="compte-bouton compte-bouton--danger" onClick={() => setEtat('confirmer')}>
          {SUPPRIMER_MON_COMPTE}
        </button>
      )}
      {(etat === 'confirmer' || etat === 'envoi') && (
        <div className="compte-confirmation" role="group" aria-label={SUPPRIMER_MON_COMPTE}>
          <p>{confirmationDeLaSuppression(email)}</p>
          <button type="button" className="compte-bouton compte-bouton--danger" disabled={etat === 'envoi'} onClick={envoyer}>
            {etat === 'envoi' ? UN_INSTANT : ENVOYER_LE_LIEN}
          </button>
          <button type="button" className="compte-bouton" disabled={etat === 'envoi'} onClick={() => setEtat('repos')}>
            {GARDER_MON_COMPTE}
          </button>
        </div>
      )}
      {etat === 'envoye' && (
        <p className="compte-message" role="status">
          {REGARDE_TA_BOITE_MAIL}
        </p>
      )}
      {typeof etat === 'object' && (
        <p className="compte-message" role="status">
          {'refus' in etat ? RAISONS[etat.refus] : etat.panne}
        </p>
      )}
      <p className="parametres-explication compte-confidentialite">
        <a href="/confidentialite">{CONFIDENTIALITE}</a>
        {CE_QUI_EST_GARDE}
      </p>
    </>
  );
}
